import 'altcha';

import { API_BASE } from '@/core/network/api';
/*
 * §9's SIGNUP FUNNEL. Registration is the ONLY way into the game, so these two
 * steps are the whole top of it: `signup_submitted` is the attempt and
 * `signup_complete` the account that exists afterwards. The collector arrives
 * through `import()` inside `engine/analytics/shell.ts`, so this costs the
 * bundle one function.
 */
import { trackFunnel } from '@/engine/analytics/shell';
import { decorateDisclosure } from '@/engine/landing/cta';
import { buttonClass } from '@/engine/ui/button';
import { attachPress } from '@/engine/ui/press';
import { formatDate, getLocale, t, tError, tParts } from '@/engine/i18n';
import {
  MIN_PASSWORD_LENGTH,
  assessPassword,
  passwordProblemKey,
  passwordProblemParams,
  type PasswordAssessment,
} from './password-policy';
import {
  REQUIRED_PURPOSES,
  fetchRequiredDocuments,
  registerWithConsent,
  type LegalDocumentSnapshot,
  type OptionalPurpose,
  type RequiredPurpose,
} from './register-api';

/**
 * REGISTRATION — the game's screen, replacing core's six-field form.
 *
 * It is here rather than in `frontend/src/core/auth/` because core's is
 * template-owned (HARD LAW 1) and lacks three things that are not decoration:
 *
 *  1. A CONFIRMED PASSWORD, shown as mismatched the moment the two fields
 *     disagree — not only after a failed submit, when the user has already been
 *     told their account was not created and does not know why.
 *  2. A PASSWORD POLICY the user can see themselves passing. The verdict comes
 *     from `password-policy.ts`, the same module the server runs, so the meter
 *     never shows green for something the server will refuse.
 *  3. CONSENT, collected properly.
 *
 * ── WHY THE CONSENT UI LOOKS LIKE THIS ──────────────────────────────────────
 *
 * • The box starts UNTICKED and there is no `checked` attribute anywhere in this
 *   file. A pre-ticked box records a decision the user never made.
 * • Pressing "Create account" is NOT acceptance. Submitting with the box clear
 *   is refused with a message, not quietly treated as agreement.
 * • The label LINKS THE DOCUMENTS, and the exact text being agreed to is
 *   expandable in place — the frozen snapshot from
 *   `GET /api/consent/documents/:purpose`, the same bytes whose hash is
 *   submitted. The in-page copy matters because `/legal/:slug` may not be routed
 *   yet, and a link to a page that does not exist is not disclosure.
 * • ONE required checkbox covers BOTH required documents, because they are one
 *   act — you cannot hold an account under the terms but outside the privacy
 *   policy — and the catalogue's `terms.accept_label` is a single sentence
 *   naming both. Each still produces its OWN consent row with its OWN version
 *   and hash, and both versions are printed under the box so the tick is
 *   specific. What is forbidden, and is not done here, is bundling an OPTIONAL
 *   consent into that tick.
 * • MARKETING IS A SEPARATE BOX that blocks nothing. Refusing it registers you
 *   just the same. (ANALYTICS, the other optional purpose, is deliberately NOT
 *   offered here: analytics consent has to be asked before analytics runs — on
 *   first visit — not at the end of a registration form. See the note at
 *   `OPTIONAL_OFFERED` below.)
 * • The version and hash of what was displayed travel with the submission, so a
 *   publish landing between render and submit is answered with a 409 and a
 *   re-prompt rather than recorded as consent to text nobody saw.
 *
 * ── WHY THE SUBMIT BUTTON IS NEVER `disabled` ───────────────────────────────
 * A greyed-out button that will not say why is the single most common
 * accessibility failure on a signup form: a screen-reader user tabs to it,
 * hears "dimmed", and has no route to the reason. It stays operable, and
 * pressing it moves focus to the first thing that is wrong and names it.
 */

/**
 * The optional consents this screen offers.
 *
 * MARKETING_EMAIL only. `ANALYTICS` exists in the model and is deliberately not
 * here: consent to analytics must be obtained before any analytics processing
 * begins, which is the first page view, not the registration form — asking for
 * it here would either be too late to be meaningful or imply that analytics had
 * not already run. It belongs to a cookie/consent banner, and the consent
 * service already accepts it from `POST /api/consent` when that exists.
 */
const OPTIONAL_OFFERED: readonly OptionalPurpose[] = ['MARKETING_EMAIL'];

const FIELD =
  'w-full min-h-[44px] px-3 py-2.5 bg-surface text-text border border-border rounded ' +
  'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold transition-colors duration-[120ms] focus:border-gold focus:shadow-glow ' +
  'aria-[invalid=true]:border-error';
const LABEL = 'block mb-1.5 text-[13px] text-text-dim';
const HINT = 'mt-1 text-[12px] text-text-dim';
const ERR = 'mt-1 text-[12px] text-error';

type El = HTMLElementTagNameMap;

function el<K extends keyof El>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): El[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  for (const c of children) node.append(typeof c === 'string' ? document.createTextNode(c) : c);
  return node;
}

/** A labelled text input with a hint slot and an error slot, wired for a11y. */
function field(opts: {
  id: string;
  type: string;
  label: string;
  autocomplete: string;
  required: boolean;
  hint?: string;
  extra?: Record<string, string>;
}): { wrap: HTMLElement; input: HTMLInputElement; error: HTMLElement } {
  const errorId = `${opts.id}-error`;
  const hintId = `${opts.id}-hint`;
  const describedBy = opts.hint !== undefined ? `${hintId} ${errorId}` : errorId;

  const input = el('input', {
    id: opts.id,
    name: opts.id,
    type: opts.type,
    autocomplete: opts.autocomplete,
    class: FIELD,
    'aria-describedby': describedBy,
    ...(opts.required ? { required: '' } : {}),
    ...(opts.extra ?? {}),
  });

  const label = el('label', { class: LABEL, for: opts.id }, [opts.label]);
  const error = el('p', { id: errorId, class: ERR, hidden: '' });
  const children: Node[] = [label, input];
  if (opts.hint !== undefined) children.push(el('p', { id: hintId, class: HINT }, [opts.hint]));
  children.push(error);

  return { wrap: el('div', { class: 'mb-4' }, children), input, error };
}

/** A checkbox whose whole row is a 44px tap target. */
function checkbox(id: string, labelContent: (Node | string)[]): {
  wrap: HTMLElement;
  input: HTMLInputElement;
} {
  const input = el('input', {
    id,
    name: id,
    type: 'checkbox',
    // NO `checked`. Consent starts absent, always.
    class: 'mt-0.5 h-6 w-6 shrink-0 accent-gold cursor-pointer',
  });
  const label = el(
    'label',
    { for: id, class: 'flex items-start gap-3 min-h-[44px] py-2 text-[13px] text-text cursor-pointer' },
    [input, el('span', {}, labelContent)],
  );
  return { wrap: el('div', {}, [label]), input };
}

function setError(input: HTMLInputElement, slot: HTMLElement, message: string | null): void {
  if (message === null) {
    slot.hidden = true;
    slot.textContent = '';
    input.removeAttribute('aria-invalid');
    return;
  }
  slot.textContent = message;
  slot.hidden = false;
  input.setAttribute('aria-invalid', 'true');
}

/**
 * Render the register screen.
 *
 * @param onSuccess called once the account exists and the session cookies are
 *        set — `main.ts` routes to /check-email, because core creates every
 *        account with `emailVerified: false` and sends the verification mail.
 */
export function renderRegister(root: HTMLElement, onSuccess: () => void): () => void {
  const abort = new AbortController();
  let disposed = false;

  /* ─────────────────────────────────────────────────────────── the markup ── */

  const form = el('form', {
    id: 'register-form',
    novalidate: '',
    class: 'w-full max-w-[480px] bg-auth-card border border-border rounded-lg p-6 sm:p-8 shadow-lift',
  });

  form.append(
    el('h1', { class: 'mb-2 font-heading text-2xl text-gold tracking-wide' }, [
      t('auth.register.title'),
    ]),
    el('p', { class: 'mb-5 text-sm text-text-dim' }, [t('auth.register.subtitle')]),
  );

  const formError = el('div', {
    id: 'register-error',
    role: 'alert',
    class: 'mb-4 px-3 py-2.5 bg-error/[0.08] border border-error/40 rounded text-error text-[13px]',
    hidden: '',
  });
  form.append(formError);

  const username = field({
    id: 'register-username',
    type: 'text',
    label: t('auth.register.username'),
    autocomplete: 'username',
    required: false,
    hint: t('auth.register.username_hint'),
    extra: { minlength: '3', maxlength: '32', pattern: '[a-zA-Z0-9_-]+' },
  });

  const email = field({
    id: 'register-email',
    type: 'email',
    label: t('auth.register.email'),
    autocomplete: 'email',
    required: true,
    extra: { inputmode: 'email', maxlength: '254' },
  });

  const password = field({
    id: 'register-password',
    type: 'password',
    label: t('auth.register.password'),
    autocomplete: 'new-password',
    required: true,
    extra: { minlength: String(MIN_PASSWORD_LENGTH), maxlength: '128' },
  });

  const confirm = field({
    id: 'register-confirm-password',
    type: 'password',
    label: t('auth.register.confirm_password'),
    autocomplete: 'new-password',
    required: true,
    extra: { maxlength: '128' },
  });

  /* ── the strength meter ──────────────────────────────────────────────────
   * `role="progressbar"` with a text `aria-valuetext`: the bar is decoration,
   * the words are the message. `aria-live="polite"` on the text so a screen
   * reader hears the rung change without being interrupted per keystroke. */
  const meterFill = el('div', {
    class: 'h-full w-0 rounded bg-border transition-[width] duration-[160ms]',
  });
  const meterBar = el(
    'div',
    {
      role: 'progressbar',
      'aria-valuemin': '0',
      'aria-valuemax': '3',
      'aria-valuenow': '0',
      'aria-label': t('auth.password_rules.strength_label'),
      class: 'h-1.5 w-full bg-surface border border-border rounded overflow-hidden',
    },
    [meterFill],
  );
  const meterText = el('p', { class: HINT, 'aria-live': 'polite' });
  const rules = el('ul', { class: 'mt-1 pl-5 list-disc text-[12px] text-text-dim' });
  const meter = el('div', { class: 'mb-4 -mt-2' }, [meterBar, meterText, rules]);

  form.append(username.wrap, email.wrap, password.wrap, meter, confirm.wrap);

  /* ── consent ─────────────────────────────────────────────────────────── */

  const consentFieldset = el('fieldset', {
    class: 'mb-4 border border-border rounded p-3',
  });
  consentFieldset.append(
    el('legend', { class: 'px-1 text-[13px] text-text-dim' }, [t('terms.legend')]),
  );

  const consentStatus = el('p', { class: HINT, 'aria-live': 'polite' }, [t('common.loading')]);
  const documentsBox = el('div', {});
  const accept = checkbox('register-accept-terms', []);
  accept.input.setAttribute('aria-describedby', 'register-accept-terms-error register-versions');
  const versions = el('p', { id: 'register-versions', class: HINT });
  const acceptError = el('p', {
    id: 'register-accept-terms-error',
    class: ERR,
    hidden: '',
  });

  const marketing = checkbox('register-accept-marketing', [t('terms.marketing_label')]);
  const optionalBox = el('div', { class: 'mt-2 pt-2 border-t border-border' }, [marketing.wrap]);

  consentFieldset.append(
    consentStatus,
    accept.wrap,
    acceptError,
    versions,
    documentsBox,
    optionalBox,
    el('p', { class: HINT }, [t('terms.recorded_note')]),
  );
  form.append(consentFieldset);

  /* ── captcha, submit, footer ─────────────────────────────────────────── */

  const captchaSlot = el('div', { class: 'mb-4' });
  const widget = document.createElement('altcha-widget');
  widget.setAttribute('challengeurl', `${API_BASE}/auth/altcha-challenge`);
  // `challenge` is what core's screen uses on this version of the component;
  // set both so the widget works whichever attribute it reads.
  widget.setAttribute('challenge', `${API_BASE}/auth/altcha-challenge`);
  widget.setAttribute('auto', 'onload');
  widget.setAttribute('name', 'altcha');
  captchaSlot.append(widget);

  const submit = el(
    'button',
    {
      type: 'submit',
      id: 'register-submit',
      // The gold CTA of the shared skin: face, rim, lip, the 95 % press and
      // the gold focus ring the old hand-rolled recipe never had.
      class: buttonClass('primary', 'lg', 'w-full min-h-[44px] p-3 font-bold'),
      'data-sfx': 'confirm',
    },
    [t('auth.register.submit')],
  );
  // Feedback only: the form's own submit is the activation.
  attachPress(submit, { onPress: () => undefined });
  /** Busy: disabled, aria-busy, a spinner — a second tap cannot register twice. */
  const setSubmitting = (on: boolean): void => {
    submit.disabled = on;
    submit.textContent = t(on ? 'auth.register.submitting' : 'auth.register.submit');
    submit.classList.toggle('is-loading', on);
    if (on) {
      submit.setAttribute('aria-busy', 'true');
      submit.append(el('span', { class: 'rga-btn__spinner', 'aria-hidden': 'true' }));
    } else {
      submit.removeAttribute('aria-busy');
    }
  };

  const footer = el('p', { class: 'mt-5 text-[13px] text-text-dim text-center' }, [
    `${t('auth.register.have_account')} `,
    el('a', { href: '/login', class: 'text-gold underline min-h-[44px]' }, [
      t('auth.register.sign_in'),
    ]),
  ]);

  form.append(captchaSlot, submit, footer);

  root.replaceChildren(
    el(
      'div',
      {
        class:
          // `min-h-[70dvh]`, not `min-h-screen`. The site header and footer are
          // now siblings of `#app` (see `engine/layout/chrome.ts`), so a screen
          // that demands a full viewport for itself guarantees the footer — the
          // one that carries the terms and privacy links this very form asks
          // the user to agree to — is pushed off the bottom on every phone.
          'min-h-[70dvh] grid place-items-center px-4 py-8 bg-auth-gradient ' +
          '[padding-left:max(1rem,env(safe-area-inset-left))] ' +
          '[padding-right:max(1rem,env(safe-area-inset-right))] ' +
          '[padding-bottom:max(2rem,env(safe-area-inset-bottom))]',
      },
      [form],
    ),
  );

  /* ────────────────────────────────────────────────────────── the wiring ── */

  /** The frozen snapshots, once loaded. `null` until then. */
  let documents: Readonly<Record<RequiredPurpose, LegalDocumentSnapshot>> | null = null;
  /** True once the operator's documents are known to be missing. */
  let documentsUnavailable = false;
  /** Only show the confirm mismatch once the user has been in that field. */
  let confirmTouched = false;
  let submitted = false;

  const renderMeter = (verdict: PasswordAssessment): void => {
    const width = ['0%', '33%', '66%', '100%'][verdict.score];
    const tone = ['bg-error', 'bg-warning', 'bg-gold', 'bg-success'][verdict.score];
    meterFill.className = `h-full rounded transition-[width] duration-[160ms] ${tone}`;
    meterFill.style.width = password.input.value.length === 0 ? '0%' : width;
    meterBar.setAttribute('aria-valuenow', String(verdict.score));

    const strength = t(`auth.password_rules.strength.${verdict.strength}`);
    meterText.textContent =
      password.input.value.length === 0
        ? t('auth.password_rules.length', { count: MIN_PASSWORD_LENGTH })
        : `${t('auth.password_rules.strength_label')}: ${strength}`;
    meterBar.setAttribute('aria-valuetext', strength);

    // Problems as a list, so every reason is shown at once rather than one at a
    // time — a user fixing them one per submit is a user who gives up.
    rules.replaceChildren(
      ...verdict.problems
        // `blank` is the empty field, which the required-field error already
        // covers; repeating it would shout at someone who has not typed yet.
        .filter((p) => p !== 'blank')
        .map((p) => el('li', {}, [t(passwordProblemKey(p), passwordProblemParams(p))])),
    );
  };

  const currentVerdict = (): PasswordAssessment =>
    assessPassword(password.input.value, {
      email: email.input.value,
      username: username.input.value,
    });

  const refreshConfirm = (): void => {
    if (!confirmTouched && !submitted) return;
    const mismatch =
      confirm.input.value.length > 0 && confirm.input.value !== password.input.value;
    setError(confirm.input, confirm.error, mismatch ? t('auth.register.confirm_mismatch') : null);
  };

  const onPasswordInput = (): void => {
    renderMeter(currentVerdict());
    refreshConfirm();
  };
  const onConfirmInput = (): void => {
    confirmTouched = true;
    refreshConfirm();
  };
  const onAcceptChange = (): void => {
    if (accept.input.checked) {
      acceptError.hidden = true;
      accept.input.removeAttribute('aria-invalid');
    }
  };

  password.input.addEventListener('input', onPasswordInput);
  confirm.input.addEventListener('input', onConfirmInput);
  confirm.input.addEventListener('blur', onConfirmInput);
  accept.input.addEventListener('change', onAcceptChange);
  renderMeter(currentVerdict());

  /* ── load the documents ──────────────────────────────────────────────── */

  const showDocuments = (docs: Readonly<Record<RequiredPurpose, LegalDocumentSnapshot>>): void => {
    documents = docs;
    consentStatus.hidden = true;

    // The sentence stays ONE translatable unit; `tParts` puts the anchors where
    // the translator's word order wants them. Never `innerHTML` a translation.
    const linkFor = (purpose: RequiredPurpose, label: string): HTMLElement =>
      el(
        'a',
        {
          href: `/legal/${docs[purpose].slug}`,
          target: '_blank',
          rel: 'noopener',
          class: 'text-gold underline',
        },
        [label],
      );

    const parts = tParts('terms.accept_label', {
      terms: t('terms.terms_link'),
      privacy: t('terms.privacy_link'),
    });
    const labelSpan = accept.input.parentElement?.querySelector('span');
    labelSpan?.replaceChildren(
      ...parts.map((part) => {
        if (part.kind === 'text') return document.createTextNode(part.text);
        if (part.name === 'terms') return linkFor('TERMS_OF_SERVICE', part.value);
        if (part.name === 'privacy') return linkFor('PRIVACY_POLICY', part.value);
        return document.createTextNode(part.value);
      }),
    );

    // Which two versions this single tick covers, stated per document.
    versions.replaceChildren(
      ...REQUIRED_PURPOSES.map((purpose) =>
        el('span', { class: 'block' }, [
          `${docs[purpose].title} — ${t('terms.version_note', {
            version: docs[purpose].version,
            date: formatDate(docs[purpose].effectiveFrom),
          })}`,
        ]),
      ),
    );

    // The exact bytes being agreed to, in the page. A link alone is not
    // disclosure when the linked route may not exist yet.
    documentsBox.replaceChildren(
      ...REQUIRED_PURPOSES.map((purpose) =>
        el('details', { class: 'mt-2 text-[12px]' }, [
          decorateDisclosure(el('summary', { class: 'min-h-[44px] py-2 text-text-dim cursor-pointer' }, [
            `${t('terms.review_link')}: ${docs[purpose].title}`,
          ])),
          el(
            'div',
            {
              class:
                'max-h-56 overflow-y-auto whitespace-pre-wrap border border-border rounded p-2 text-text-dim',
            },
            // textContent, not innerHTML: the body is operator-authored text and
            // this screen is not the place to execute it.
            [docs[purpose].content],
          ),
        ]),
      ),
    );
  };

  const blockOnUnpublished = (): void => {
    documentsUnavailable = true;
    consentStatus.hidden = false;
    consentStatus.className = ERR;
    consentStatus.textContent = t('terms.documents_unavailable');
    accept.input.disabled = true;
  };

  void (async () => {
    try {
      const loaded = await fetchRequiredDocuments(abort.signal);
      if (disposed) return;
      const missing = REQUIRED_PURPOSES.filter((p) => loaded[p] === null);
      if (missing.length > 0) {
        blockOnUnpublished();
        return;
      }
      showDocuments(loaded as Readonly<Record<RequiredPurpose, LegalDocumentSnapshot>>);
    } catch (err) {
      if (disposed) return;
      // A failure to LOAD is not "the operator forgot to publish". Say what
      // actually happened and let the user retry.
      consentStatus.hidden = false;
      consentStatus.className = ERR;
      consentStatus.textContent = tError(err);
    }
  })();

  /* ── submit ──────────────────────────────────────────────────────────── */

  const fail = (message: string, focus: HTMLElement): void => {
    formError.textContent = message;
    formError.hidden = false;
    focus.focus();
  };

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    submitted = true;
    formError.hidden = true;
    setError(email.input, email.error, null);
    setError(password.input, password.error, null);

    if (email.input.value.trim().length === 0) {
      setError(email.input, email.error, t('errors.validation.required'));
      fail(t('errors.validation.required'), email.input);
      return;
    }

    // 1. The password must clear the policy. Same function the server runs.
    const verdict = currentVerdict();
    renderMeter(verdict);
    if (!verdict.acceptable) {
      const first = verdict.problems[0];
      const reason =
        first === undefined
          ? t('errors.code.password_too_weak')
          : t(passwordProblemKey(first), passwordProblemParams(first));
      setError(password.input, password.error, reason);
      fail(reason, password.input);
      return;
    }

    // 2. The two passwords must match. Inline already, and again here.
    if (password.input.value !== confirm.input.value) {
      confirmTouched = true;
      setError(confirm.input, confirm.error, t('auth.register.confirm_mismatch'));
      fail(t('auth.register.confirm_mismatch'), confirm.input);
      return;
    }

    // 3. Nothing to accept means nothing can be created.
    if (documentsUnavailable || documents === null) {
      fail(t('terms.documents_unavailable'), submit);
      return;
    }

    // 4. THE TICK. Pressing this button is not acceptance; the box is.
    if (!accept.input.checked) {
      acceptError.textContent = t('terms.required_error');
      acceptError.hidden = false;
      accept.input.setAttribute('aria-invalid', 'true');
      fail(t('terms.required_error'), accept.input);
      return;
    }

    // 5. The captcha core requires.
    const altcha = new FormData(form).get('altcha');
    const altchaPayload = typeof altcha === 'string' ? altcha : '';
    if (altchaPayload.length === 0) {
      fail(t('auth.register.captcha_pending'), submit);
      return;
    }

    const optionalConsents = OPTIONAL_OFFERED.filter(
      (p) => p === 'MARKETING_EMAIL' && marketing.input.checked,
    );

    setSubmitting(true);
    // The DENOMINATOR of the completion rate: fired on the attempt, before the
    // round trip, so a registration that fails is still counted as one asked for.
    trackFunnel('signup_submitted');
    try {
      await registerWithConsent({
        email: email.input.value.trim(),
        password: password.input.value,
        confirmPassword: confirm.input.value,
        username: username.input.value.trim(),
        altcha: altchaPayload,
        // Exactly what was displayed: the version and the hash of the snapshot
        // rendered above. The server refuses the pair if either has moved.
        acceptedDocuments: REQUIRED_PURPOSES.map((purpose) => ({
          purpose,
          documentVersion: documents![purpose].version,
          documentHash: documents![purpose].contentHash,
        })),
        optionalConsents,
        locale: getLocale(),
      });
      trackFunnel('signup_complete');
      onSuccess();
    } catch (err) {
      formError.textContent = tError(err);
      formError.hidden = false;
      setSubmitting(false);
      submit.focus();
    }
  };

  const submitHandler = (e: Event): void => {
    void onSubmit(e);
  };
  form.addEventListener('submit', submitHandler);

  return () => {
    disposed = true;
    abort.abort();
    form.removeEventListener('submit', submitHandler);
    password.input.removeEventListener('input', onPasswordInput);
    confirm.input.removeEventListener('input', onConfirmInput);
    confirm.input.removeEventListener('blur', onConfirmInput);
    accept.input.removeEventListener('change', onAcceptChange);
  };
}
